import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Calendar, Plus } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";

// Roles allowed to access the create form on /club. Mirrors the
// `roles=` array on the `<Route path="/club">` so we don't show a button
// that would just bounce the user back to /dashboard.
const CAN_CREATE_TOURNAMENT = new Set(["club_admin", "admin", "coach", "federation"]);

export default function Tournaments() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [mine, setMine] = useState(false);
  useEffect(() => {
    api.get(`/tournaments${mine ? "?mine=true" : ""}`).then((r) => setItems(r.data));
  }, [mine]);
  const canCreate = user && CAN_CREATE_TOURNAMENT.has(user.role);
  return (
    <AppLayout>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black">{t("tournaments")}</h1>
          <p className="text-zinc-400 mt-2">Compete. Win. Climb the ranks.</p>
        </div>
        <div className="flex items-center gap-2">
          {canCreate && (
            <div className="inline-flex rounded-xl bg-zinc-900 border border-zinc-800 p-1" data-testid="tournaments-mine-toggle">
              <button data-testid="tournaments-tab-all" onClick={() => setMine(false)} className={`px-3 py-2 rounded-lg text-sm ${!mine ? "bg-zinc-800 text-white" : "text-zinc-400 hover:text-white"}`}>All</button>
              <button data-testid="tournaments-tab-mine" onClick={() => setMine(true)} className={`px-3 py-2 rounded-lg text-sm ${mine ? "bg-zinc-800 text-white" : "text-zinc-400 hover:text-white"}`}>My events</button>
            </div>
          )}
          {canCreate && (
            <Link to="/club" data-testid="tournaments-create" className="pp-btn-primary px-5 py-3 rounded-xl flex items-center gap-2">
              <Plus size={18} /> {t("create_tournament")}
            </Link>
          )}
        </div>
      </div>
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="tournaments-grid">
        {items.map((tour) => {
          const accent = tour.brand_color || "#007AFF";
          const priceAccent = tour.brand_color || "#00E676";
          return (
          <Link key={tour.id} to={`/tournaments/${tour.id}`} data-testid={`tour-card-${tour.id}`} className="pp-card p-6 block">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider flex items-center gap-1" style={{ color: accent }}><Trophy size={14} /> {tour.sport?.name}</div>
              <span className="pp-chip">{tour.status}</span>
            </div>
            <div className="font-heading text-xl font-bold mt-2">{tour.title}</div>
            <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{tour.description}</p>
            <div className="mt-4 flex items-center justify-between text-xs text-zinc-500">
              <span className="flex items-center gap-1"><Calendar size={12} /> {tour.all_day ? new Date(tour.start_date).toLocaleDateString() : new Date(tour.start_date).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
              <span className="font-semibold" style={{ color: priceAccent }}>{tour.entry_fee > 0 ? `${tour.entry_fee} ${tour.currency}` : t("free")}</span>
            </div>
          </Link>
          );
        })}
        {items.length === 0 && <div className="pp-card p-10 text-zinc-500 col-span-full text-center" data-testid="tournaments-empty">{mine ? "You haven't created any tournaments yet." : t("no_results")}</div>}
      </div>
    </AppLayout>
  );
}
